/*------------------------------------------------------------------------------
   About      : "Pages & Actions" category tab — grants against the FE-owned
                 UI_PAGES registry (RBAC v2 access-control revamp)

   Rendered FROM the registry (UiRegistry.ts), never from the backend. The
   hierarchy shown is section → page → CTAs, and under each page/CTA the
   proxypulse API actions (`apis`) it depends on — so an admin grants
   visibility (ui.* id) and capability (API feature) in one place.

   Grant mechanics:
   - ui.* ids are lazily materialized as DAFeature rows on first grant
     (create-feature + file into 'ui_registry' group), then granted/revoked
     with the normal role/user feature APIs.
   - API rows reference features proxypulse auto-discovered from its routes;
     they are granted by feature id. If a listed API feature is missing from
     the backend ("not synced"), its row is disabled.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  alpha,
} from '@mui/material';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { RbacApis } from 'apis/rbacAPI';
import { selectUiPermissionSlice, RbacEntityType } from 'store/dataReducers/rbacSlice';
import { UI_PAGES, UI_SECTION_LABELS, UiPageDef, UiCtaDef, UiSection, isUiEndpoint } from 'framework/access/UiRegistry';
import { AnnotatedFeature } from 'types/apis/rbacInterfaces';

const chipSx = { height: 18, fontSize: 11 } as const;
const pendingRowBg = { bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.08) };

const matchText = (value: string, term: string) => value.toLowerCase().includes(term);

const pageMatches = (page: UiPageDef, term: string) =>
  matchText(page.label, term)
  || Object.values(page.ctas).some((cta) => matchText(cta.label, term) || (cta.apis || []).some((a) => matchText(a, term)))
  || (page.apis || []).some((a) => matchText(a, term));

export interface UiAccessPanelProps {
  entityType: RbacEntityType;
  entityId: number | string;
  onSaved: () => void | Promise<void>;
}

export const UiAccessPanel = ({ entityType, entityId, onSaved }: UiAccessPanelProps) => {
  const { rc } = useGlobalConfig();
  const toast = useToast();

  const { allByEndpoint, orphans } = useSelector(selectUiPermissionSlice);

  const [pending, setPending] = useState<Map<string, boolean>>(new Map());
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const collapsiblePageIds = useMemo(
    () =>
      Object.values<UiPageDef>(UI_PAGES)
        .filter((page) => Object.keys(page.ctas).length > 0 || (page.apis || []).length > 0)
        .map((page) => page.id),
    [],
  );

  // All pages collapsed by default — admin expands only what they're editing.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(collapsiblePageIds));

  const handleExpandAll = () => setCollapsed(new Set());
  const handleCollapseAll = () => setCollapsed(new Set(collapsiblePageIds));
  const togglePageExpand = (pageId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });

  const serverGranted = (endpointId: string) =>
    allByEndpoint.get(endpointId)?.grant_state === 'granted';

  const isGranted = (endpointId: string) => {
    const override = pending.get(endpointId);
    if (override !== undefined) return override;
    return serverGranted(endpointId);
  };

  const setPendingChange = (endpointId: string, grant: boolean) => {
    setPending((prev) => {
      const next = new Map(prev);
      if (grant === serverGranted(endpointId)) next.delete(endpointId);
      else next.set(endpointId, grant);
      return next;
    });
  };

  const queueChanges = (endpointIds: string[], grant: boolean) => {
    setPending((prev) => {
      const next = new Map(prev);
      endpointIds.forEach((endpointId) => {
        if (grant === serverGranted(endpointId)) next.delete(endpointId);
        else next.set(endpointId, grant);
      });
      return next;
    });
  };

  /** API row is grantable only when proxypulse auto-discovery created the feature. */
  const apiIsSynced = (api: string) => allByEndpoint.has(api);

  // Toggling a page/CTA cascades to everything nested under it (actions + API
  // permissions) in both directions, so the tree behaves like a standard
  // tri-state checkbox tree. Unsynced API rows are skipped on grant (the
  // backend feature doesn't exist yet) but always cleared on revoke.
  const handleTogglePage = (page: UiPageDef, grant: boolean) => {
    const ctas = Object.values(page.ctas);
    const apis = [
      ...(page.apis || []),
      ...ctas.flatMap((cta) => cta.apis || []),
    ];
    const cascadeApis = grant ? apis.filter(apiIsSynced) : apis;
    queueChanges([page.id, ...ctas.map((cta) => cta.id), ...cascadeApis], grant);
  };

  const handleToggleCta = (cta: UiCtaDef, grant: boolean) => {
    const apis = grant ? (cta.apis || []).filter(apiIsSynced) : (cta.apis || []);
    queueChanges([cta.id, ...apis], grant);
  };

  const handleToggleApi = (api: string, grant: boolean) => setPendingChange(api, grant);

  const handleRevokeOrphan = (feature: AnnotatedFeature) => setPendingChange(feature.endpoint, false);

  const handleDiscard = () => setPending(new Map());

  const grantOrRevoke = async (featureId: number, grant: boolean) => {
    if (entityType === 'role') {
      return grant
        ? RbacApis.attachRoleFeatures(rc, entityId, [featureId])
        : RbacApis.detachRoleFeatures(rc, entityId, [featureId]);
    }
    return grant
      ? RbacApis.grantUserFeature(rc, entityId, featureId)
      : RbacApis.revokeUserFeature(rc, entityId, featureId);
  };

  const materialize = async (endpointId: string, label: string): Promise<number | undefined> => {
    const groupResp = await RbacApis.ensureUiRegistryGroup(rc);
    if (!groupResp.success || !groupResp.data) return undefined;

    const createResp = await RbacApis.createFeature(rc, {
      endpoint : endpointId,
      path     : `ui://${endpointId}`,
      methods  : 'UI',
      domain   : 'ui',
      label,
    });
    if (!createResp.success || !createResp.data) return undefined;

    await RbacApis.addFeaturesToGroup(rc, groupResp.data.id, [createResp.data.id]);
    return createResp.data.id;
  };

  const handleSave = async () => {
    if (pending.size === 0) return;
    setSaving(true);

    const labelFor = (endpointId: string): string => {
      for (const page of Object.values(UI_PAGES)) {
        if (page.id === endpointId) return page.label;
        const cta = Object.values(page.ctas).find((c) => c.id === endpointId);
        if (cta) return `${page.label} · ${cta.label}`;
      }
      return allByEndpoint.get(endpointId)?.label || endpointId;
    };

    const errors: string[] = [];
    for (const [endpointId, grant] of pending) {
      const existing = allByEndpoint.get(endpointId);
      let featureId = existing?.id;

      if (!featureId) {
        if (!grant) continue; // revoking something that has no backend row — nothing to do
        if (!isUiEndpoint(endpointId)) {
          // API features are owned by proxypulse auto-discovery — never create them here.
          errors.push(`"${labelFor(endpointId)}" is not synced to the backend yet`);
          continue;
        }
        featureId = await materialize(endpointId, labelFor(endpointId));
        if (!featureId) {
          errors.push(`Could not create "${labelFor(endpointId)}"`);
          continue;
        }
      }

      const resp = await grantOrRevoke(featureId, grant);
      if (!resp.success && resp.error) errors.push(resp.error);
    }

    if (errors.length) toast.error(`${errors.length} change(s) failed: ${errors[0]}`);
    else toast.success('Page & action access updated');

    setPending(new Map());
    await onSaved();
    setSaving(false);
  };

  const searchTerm = search.trim().toLowerCase();
  const searchActive = searchTerm.length > 0;

  const sections = useMemo(() => {
    const bySection = new Map<UiSection, UiPageDef[]>();
    Object.values(UI_PAGES).forEach((page) => {
      if (searchActive && !pageMatches(page, searchTerm)) return;
      const list = bySection.get(page.section) || [];
      list.push(page);
      bySection.set(page.section, list);
    });
    return bySection;
  }, [searchTerm, searchActive]);

  const noResults = searchActive && sections.size === 0;

  const renderApiRow = (api: string, indent: number) => {
    const synced = apiIsSynced(api);
    const checked = isGranted(api);
    const isPending = pending.has(api);
    const feature = allByEndpoint.get(api);
    return (
      <Box
        key={api}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: indent, py: 0.25, borderRadius: 1, ...(isPending ? pendingRowBg : {}) }}
      >
        <Checkbox
          size="small"
          checked={checked}
          disabled={!synced}
          onChange={(e) => handleToggleApi(api, e.target.checked)}
        />
        <Chip size="small" variant="outlined" label="API" sx={{ ...chipSx, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">{feature?.label || api}</Typography>
        {!synced && <Chip size="small" variant="outlined" label="not synced" sx={{ ...chipSx, color: 'text.secondary' }} />}
        {isPending && <Chip size="small" color="primary" variant="outlined" label="pending" sx={chipSx} />}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      <Box sx={{ p: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          sx={{ flexGrow: 1 }}
          placeholder="Search pages, actions & API permissions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Button size="small" onClick={handleExpandAll} disabled={searchActive} sx={{ whiteSpace: 'nowrap' }}>
          Expand all
        </Button>
        <Button size="small" onClick={handleCollapseAll} disabled={searchActive} sx={{ whiteSpace: 'nowrap' }}>
          Collapse all
        </Button>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pb: 1 }}>
        {Array.from(sections.entries()).map(([section, pages]) => (
          <Box key={section} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              {UI_SECTION_LABELS[section]}
            </Typography>
            {pages.map((page) => {
              const ctas = Object.values(page.ctas);
              const grantedCtaCount = ctas.filter((cta) => isGranted(cta.id)).length;
              const pageChecked = isGranted(page.id);
              const nestedApis = [...(page.apis || []), ...ctas.flatMap((cta) => cta.apis || [])];
              const indeterminate = !pageChecked && (grantedCtaCount > 0 || nestedApis.some(isGranted));
              const pageIsPending = pending.has(page.id);
              const materializedFeature = allByEndpoint.get(page.id);
              const viaRole = entityType === 'user' && materializedFeature
                && !materializedFeature.granted_directly && !!materializedFeature.granted_via_role_id;

              const hasChildren = ctas.length > 0 || (page.apis || []).length > 0;
              const isExpanded = searchActive || !collapsed.has(page.id);
              const hiddenPending = !isExpanded && [
                ...(page.apis || []),
                ...ctas.flatMap((cta) => [cta.id as string, ...(cta.apis || [])]),
              ].some((id) => pending.has(id));

              return (
                <Box key={page.id} sx={{ mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, borderRadius: 1, ...(pageIsPending ? pendingRowBg : {}) }}>
                    <IconButton
                      size="small"
                      onClick={() => togglePageExpand(page.id)}
                      disabled={searchActive}
                      sx={{ p: 0.25, visibility: hasChildren ? 'visible' : 'hidden' }}
                    >
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </IconButton>
                    <Checkbox
                      size="small"
                      checked={pageChecked}
                      indeterminate={indeterminate}
                      onChange={(e) => handleTogglePage(page, e.target.checked)}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{page.label}</Typography>
                    {ctas.length > 0 && (
                      <Chip size="small" variant="outlined" label={`${grantedCtaCount}/${ctas.length}`} sx={{ ...chipSx, color: 'text.secondary' }} />
                    )}
                    {!page.enforced && (
                      <Chip size="small" variant="outlined" label="not enforced" sx={{ ...chipSx, color: 'text.secondary' }} />
                    )}
                    {viaRole && <Chip size="small" variant="outlined" label="via role" sx={{ ...chipSx, color: 'text.secondary' }} />}
                    {pageIsPending && <Chip size="small" color="primary" variant="outlined" label="pending" sx={chipSx} />}
                    {hiddenPending && <Chip size="small" color="primary" variant="outlined" label="pending inside" sx={chipSx} />}
                  </Box>

                  {isExpanded && (
                    <>
                      {(page.apis || []).map((api) => renderApiRow(api, 5))}

                      {ctas.map((cta) => {
                        const ctaChecked = isGranted(cta.id);
                        const ctaIndeterminate = !ctaChecked && (cta.apis || []).some(isGranted);
                        const ctaIsPending = pending.has(cta.id);
                        return (
                          <Box key={cta.id}>
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 5, py: 0.25, borderRadius: 1, ...(ctaIsPending ? pendingRowBg : {}) }}
                            >
                              <Checkbox
                                size="small"
                                checked={ctaChecked}
                                indeterminate={ctaIndeterminate}
                                onChange={(e) => handleToggleCta(cta, e.target.checked)}
                              />
                              <Typography variant="body2" color="text.secondary">{cta.label}</Typography>
                              {ctaIsPending && <Chip size="small" color="primary" variant="outlined" label="pending" sx={chipSx} />}
                            </Box>
                            {(cta.apis || []).map((api) => renderApiRow(api, 9))}
                          </Box>
                        );
                      })}
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}

        {noResults && (
          <Typography variant="body2" color="text.secondary">
            No pages, actions or API permissions match "{search.trim()}".
          </Typography>
        )}

        {orphans.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Orphaned grants
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Granted, but no longer present in the app's page/action registry (renamed or removed). Revoke-only.
            </Typography>
            {orphans.map((feature) => {
              const checked = isGranted(feature.endpoint);
              const isPending = pending.has(feature.endpoint);
              return (
                <Box key={feature.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, borderRadius: 1, ...(isPending ? pendingRowBg : {}) }}>
                  <Checkbox size="small" checked={checked} onChange={() => handleRevokeOrphan(feature)} />
                  <Typography variant="body2">{feature.label || feature.endpoint}</Typography>
                  {isPending && <Chip size="small" color="primary" variant="outlined" label="pending" sx={chipSx} />}
                </Box>
              );
            })}
          </>
        )}
      </Box>

      {pending.size > 0 && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {pending.size} pending change{pending.size > 1 ? 's' : ''}
          </Typography>
          <Button size="small" onClick={handleDiscard} disabled={saving}>
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
